import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../../components/SEO';

export default function CommercialSecurityRichmondHill() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    fetch('https://readdy.ai/api/public/form/submit/commercial-security-richmond-hill-form', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(formData as any).toString(),
    })
      .then(response => {
        if (response.ok) {
          alert('Thank you! We\'ll contact you within 24 hours to discuss your commercial security needs.');
          form.reset();
        } else {
          alert('Something went wrong. Please try again or call us directly.');
        }
      })
      .catch(() => {
        alert('Something went wrong. Please try again or call us directly.');
      });
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://alliancesecuritysystems.com/commercial-security/richmond-hill#business",
        "name": "Alliance Security Systems - Commercial Security Richmond Hill",
        "image": "https://public.readdy.ai/ai/img_res/13024dc0b79bfaeebeda0af3e10bdf6f.jpg",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Richmond Hill",
          "addressRegion": "ON",
          "addressCountry": "CA"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 43.8828,
          "longitude": -79.4403
        },
        "url": "https://alliancesecuritysystems.com/commercial-security/richmond-hill",
        "telephone": "+1-888-458-9181",
        "priceRange": "$$",
        "areaServed": {
          "@type": "City",
          "name": "Richmond Hill"
        },
        "serviceType": ["Commercial Security Systems", "Business Alarm Systems", "Access Control", "Security Cameras", "Live Monitoring"]
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://alliancesecuritysystems.com/commercial-security/richmond-hill#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://alliancesecuritysystems.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Commercial Security",
            "item": "https://alliancesecuritysystems.com/commercial-security"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Richmond Hill",
            "item": "https://alliancesecuritysystems.com/commercial-security/richmond-hill"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "@id": "https://alliancesecuritysystems.com/commercial-security/richmond-hill#faq",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What commercial security regulations apply in Richmond Hill?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Richmond Hill commercial security systems must comply with York Region bylaws, Ontario Fire Code requirements for alarm systems, and PIPEDA privacy regulations for video surveillance. All alarm systems require municipal registration, and businesses must post signage notifying employees and visitors of video monitoring. Our installations meet all local compliance requirements and include proper documentation for insurance and regulatory purposes."
            }
          },
          {
            "@type": "Question",
            "name": "How much does commercial security cost in Richmond Hill?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Commercial security systems in Richmond Hill typically range from $2,500-$8,000 for small offices (8-camera system with access control), $8,000-$25,000 for retail plazas and corporate offices (16-32 cameras with integrated alarm and access control), and $25,000+ for large corporate campuses and warehouse facilities. Monthly monitoring starts at $49.99 for basic alarm monitoring and $149.99+ for 24/7 video verification. We provide transparent quotes with no hidden fees and flexible financing options for Richmond Hill businesses."
            }
          },
          {
            "@type": "Question",
            "name": "Do you serve corporate parks in Richmond Hill like Beaver Creek Business Park?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, we specialize in securing Richmond Hill's major corporate parks including Beaver Creek Business Park, Headford Business Park, East Beaver Creek Business Park, and the Highway 7 corporate corridor. We have extensive experience with multi-tenant office buildings, corporate headquarters, and business campuses requiring integrated access control, perimeter security, parking lot surveillance, and visitor management systems. Our team understands the unique security needs of Richmond Hill's professional business community."
            }
          }
        ]
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Commercial Security Richmond Hill | Business Alarm & Camera Systems"
        description="Professional commercial security systems for Richmond Hill businesses. Protect your office, retail store, or warehouse with 24/7 monitoring, HD cameras, and access control. Serving Beaver Creek, Yonge Street corridor, and all Richmond Hill business districts. Free assessment."
        keywords="commercial security Richmond Hill, business security systems Richmond Hill, commercial alarm Richmond Hill, security cameras Richmond Hill business, access control Richmond Hill, Richmond Hill commercial security company"
        canonical="/commercial-security/richmond-hill"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[70vh] min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=modern%20corporate%20office%20building%20in%20Richmond%20Hill%20Ontario%20with%20professional%20security%20camera%20installation%20on%20glass%20facade%20daytime%20business%20district%20clean%20architectural%20photography%20style%20showing%20high%20tech%20surveillance%20equipment%20protecting%20commercial%20property&width=1920&height=1080&seq=rh-commercial-hero&orientation=landscape"
              alt="Commercial Security Systems Richmond Hill - Professional business security camera installation on modern office building"
              title="Richmond Hill Commercial Security - Business Alarm & Camera Systems"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-6 text-center text-white w-full">
            <nav className="flex justify-center mb-6" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <Link to="/" className="hover:text-teal-400 transition-colors" itemProp="item">
                    <span itemProp="name">Home</span>
                  </Link>
                  <meta itemProp="position" content="1" />
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <Link to="/commercial-security" className="hover:text-teal-400 transition-colors" itemProp="item">
                    <span itemProp="name">Commercial Security</span>
                  </Link>
                  <meta itemProp="position" content="2" />
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <span itemProp="name">Richmond Hill</span>
                  <meta itemProp="position" content="3" />
                </li>
              </ol>
            </nav>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Commercial Security Richmond Hill
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto font-light">
              Protecting Richmond Hill Businesses Since 1999 — Corporate Parks, Retail Plazas & Professional Offices
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="#contact-form" 
                className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Get Free Assessment
              </a>
              <a 
                href="tel:+18884589181" 
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold transition-all border border-white/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                1-888-458-9181
              </a>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-gray-50 py-8 border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-teal-600 mb-2">85+</div>
                <div className="text-sm text-gray-600">Richmond Hill Commercial Clients</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-600 mb-2">&lt;12 min</div>
                <div className="text-sm text-gray-600">Average Response Time</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-600 mb-2">24/7</div>
                <div className="text-sm text-gray-600">Live Monitoring</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-600 mb-2">20%</div>
                <div className="text-sm text-gray-600">Avg. Insurance Discount</div>
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Security Challenges */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Commercial Security Challenges in Richmond Hill
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                York Region's business hub faces unique security demands across corporate parks, retail corridors, and professional offices
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 rounded-xl p-8">
                <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-building-2-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Corporate Park Security</h3>
                <p className="text-gray-600 mb-4">
                  Richmond Hill's major corporate parks like Beaver Creek and Headford Business Park house high-value technology companies and professional services firms requiring sophisticated access control and perimeter security.
                </p>
                <div className="bg-white rounded-lg p-4 border-l-4 border-teal-500">
                  <div className="text-2xl font-bold text-gray-900">$45K+</div>
                  <div className="text-sm text-gray-600">Average corporate data breach cost</div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-store-2-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Retail Plaza Protection</h3>
                <p className="text-gray-600 mb-4">
                  High-traffic retail corridors along Yonge Street and Highway 7 experience elevated shoplifting and after-hours break-in attempts, requiring integrated camera systems with AI analytics and 24/7 monitoring.
                </p>
                <div className="bg-white rounded-lg p-4 border-l-4 border-teal-500">
                  <div className="text-2xl font-bold text-gray-900">38%</div>
                  <div className="text-sm text-gray-600">Retail theft increase in York Region (2024)</div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-truck-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Warehouse & Distribution</h3>
                <p className="text-gray-600 mb-4">
                  Industrial areas near Highway 404 and the East Beaver Creek corridor face cargo theft and inventory shrinkage challenges, requiring perimeter monitoring, loading dock cameras, and inventory tracking integration.
                </p>
                <div className="bg-white rounded-lg p-4 border-l-4 border-teal-500">
                  <div className="text-2xl font-bold text-gray-900">$12K</div>
                  <div className="text-sm text-gray-600">Average warehouse theft loss per incident</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Commercial Security Solutions
              </h2>
              <p className="text-xl text-gray-600">
                Comprehensive protection for Richmond Hill businesses
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link 
                to="/camera-packages" 
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all border border-gray-200 group cursor-pointer"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-camera-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Commercial Cameras</h3>
                <p className="text-gray-600 mb-4">4K resolution, AI analytics, license plate recognition for parking lots and loading docks</p>
                <div className="text-teal-600 font-semibold group-hover:text-teal-700">
                  Learn More <i className="ri-arrow-right-line ml-1"></i>
                </div>
              </Link>

              <Link 
                to="/alarm-packages" 
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all border border-gray-200 group cursor-pointer"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-alarm-warning-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Business Alarms</h3>
                <p className="text-gray-600 mb-4">Intrusion detection, glass break sensors, panic buttons with York Regional Police integration</p>
                <div className="text-teal-600 font-semibold group-hover:text-teal-700">
                  Learn More <i className="ri-arrow-right-line ml-1"></i>
                </div>
              </Link>

              <Link 
                to="/access-control" 
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all border border-gray-200 group cursor-pointer"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-key-2-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Access Control</h3>
                <p className="text-gray-600 mb-4">Keycard systems, biometric readers, visitor management for corporate offices</p>
                <div className="text-teal-600 font-semibold group-hover:text-teal-700">
                  Learn More <i className="ri-arrow-right-line ml-1"></i>
                </div>
              </Link>

              <Link 
                to="/live-monitoring" 
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all border border-gray-200 group cursor-pointer"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-eye-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Live Monitoring</h3>
                <p className="text-gray-600 mb-4">24/7 ULC-certified monitoring with video verification and emergency dispatch</p>
                <div className="text-teal-600 font-semibold group-hover:text-teal-700">
                  Learn More <i className="ri-arrow-right-line ml-1"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Business Districts Served */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Richmond Hill Business Districts We Serve
              </h2>
              <p className="text-xl text-gray-600">
                Local expertise across all major commercial areas
              </p>
            </div>

            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                'Beaver Creek Business Park',
                'Headford Business Park',
                'East Beaver Creek',
                'Yonge Street Corridor',
                'Highway 7 Corporate',
                'Langstaff Business District',
                'Elgin Mills Corporate',
                'Major Mackenzie Business',
                'Leslie Street Commercial',
                'Bayview Avenue Retail',
                'Highway 404 Industrial',
                'Richmond Hill Centre'
              ].map((district, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 rounded-lg p-4 text-center hover:bg-teal-50 transition-colors border border-gray-200"
                >
                  <i className="ri-map-pin-line text-2xl text-teal-600 mb-2"></i>
                  <div className="font-semibold text-gray-900">{district}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Richmond Hill Commercial Security FAQs
              </h2>
            </div>

            <div className="space-y-6" itemScope itemType="https://schema.org/FAQPage">
              <details className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                  <span itemProp="name">What commercial security regulations apply in Richmond Hill?</span>
                  <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="mt-4 text-gray-600 leading-relaxed" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p itemProp="text">
                    Richmond Hill commercial security systems must comply with York Region bylaws, Ontario Fire Code requirements for alarm systems, and PIPEDA privacy regulations for video surveillance. All alarm systems require municipal registration, and businesses must post signage notifying employees and visitors of video monitoring. Our installations meet all local compliance requirements and include proper documentation for insurance and regulatory purposes.
                  </p>
                </div>
              </details>

              <details className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                  <span itemProp="name">How much does commercial security cost in Richmond Hill?</span>
                  <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="mt-4 text-gray-600 leading-relaxed" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p itemProp="text">
                    Commercial security systems in Richmond Hill typically range from $2,500-$8,000 for small offices (8-camera system with access control), $8,000-$25,000 for retail plazas and corporate offices (16-32 cameras with integrated alarm and access control), and $25,000+ for large corporate campuses and warehouse facilities. Monthly monitoring starts at $49.99 for basic alarm monitoring and $149.99+ for 24/7 video verification. We provide transparent quotes with no hidden fees and flexible financing options for Richmond Hill businesses.
                  </p>
                </div>
              </details>

              <details className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                  <span itemProp="name">Do you serve corporate parks in Richmond Hill like Beaver Creek Business Park?</span>
                  <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="mt-4 text-gray-600 leading-relaxed" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p itemProp="text">
                    Yes, we specialize in securing Richmond Hill's major corporate parks including Beaver Creek Business Park, Headford Business Park, East Beaver Creek Business Park, and the Highway 7 corporate corridor. We have extensive experience with multi-tenant office buildings, corporate headquarters, and business campuses requiring integrated access control, perimeter security, parking lot surveillance, and visitor management systems. Our team understands the unique security needs of Richmond Hill's professional business community.
                  </p>
                </div>
              </details>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact-form" className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Get Your Free Security Assessment
              </h2>
              <p className="text-xl text-gray-600">
                Protecting Richmond Hill businesses for over 25 years
              </p>
            </div>

            <form 
              onSubmit={handleFormSubmit}
              className="bg-gray-50 rounded-2xl p-8 border border-gray-200"
              data-readdy-form
              id="commercial-security-richmond-hill-form"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="(416) 555-0199"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="john@company.com"
                  />
                </div>
                <div>
                  <label htmlFor="business-name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    id="business-name"
                    name="business-name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Your Company Inc."
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="industry" className="block text-sm font-semibold text-gray-700 mb-2">
                    Industry *
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select Industry</option>
                    <option value="retail">Retail</option>
                    <option value="office">Office / Corporate</option>
                    <option value="warehouse">Warehouse / Distribution</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="hospitality">Hospitality</option>
                    <option value="construction">Construction</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="locations" className="block text-sm font-semibold text-gray-700 mb-2">
                    Number of Locations
                  </label>
                  <input
                    type="number"
                    id="locations"
                    name="locations"
                    min="1"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="1"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="security-needs" className="block text-sm font-semibold text-gray-700 mb-2">
                  Security Needs *
                </label>
                <textarea
                  id="security-needs"
                  name="security-needs"
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                  placeholder="Tell us about your security requirements (max 500 characters)"
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-4 rounded-lg transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Get Free Assessment
              </button>
            </form>
          </div>
        </section>

        {/* Other Cities */}
        <section className="py-16 bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Commercial Security in Other GTA Cities
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { name: 'Toronto', path: '/commercial-security/toronto' },
                { name: 'Mississauga', path: '/commercial-security/mississauga' },
                { name: 'Brampton', path: '/commercial-security/brampton' },
                { name: 'Oakville', path: '/commercial-security/oakville' },
                { name: 'Hamilton', path: '/commercial-security/hamilton' },
                { name: 'Vaughan', path: '/commercial-security/vaughan' }
              ].map((city, index) => (
                <Link
                  key={index}
                  to={city.path}
                  className="px-6 py-3 bg-white hover:bg-teal-50 rounded-lg border border-gray-200 hover:border-teal-500 transition-all font-semibold text-gray-700 hover:text-teal-600 whitespace-nowrap cursor-pointer"
                >
                  {city.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
